import * as THREE from 'three';
import { isRevealed, type BattleState } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { WORLD_PER_HEX } from '../engine/coords.ts';
import { cameraRight, eyeHeight, looksRng, SkyLook, type OmenCtx, type OmenScene } from './common.ts';

/**
 * Brann's bet «A oscuras» (docs/SAGAS.md): a moonless night. The board goes blue-black under a sheet of
 * darkness laid over the ground, with holes where the light is: a warm pool round every standing hero's
 * lantern and the node's brazier. Out in the dark the nightmares are only pairs of eyes; moths circle the
 * lanterns. One shader quad grid and one glow layer: no lights are added to the scene (docs/RENDIMIENTO.md).
 */

/** The lantern's reach (BET.lantern in the sim, 2.6 hexes) and the node's light (KEEP_LIGHT, 2.5 hexes), in world units. */
export const NIGHT_LANTERN = 2.6 * WORLD_PER_HEX;
const NODE_LIGHT = 2.5 * WORLD_PER_HEX;
const MAX_LIGHTS = 12;

const VERT = /* glsl */ `
varying vec2 vW;
void main() {
  vec4 w = modelMatrix * vec4(position, 1.0);
  vW = w.xz;
  gl_Position = projectionMatrix * viewMatrix * w;
}`;

const FRAG = /* glsl */ `
uniform vec4 uLights[${MAX_LIGHTS}]; // x, z, radius, kind (1: a hero's lantern, 2: the node's brazier)
uniform int uCount;
uniform float uDark;
uniform float uTime;
uniform vec3 uNight;
uniform vec3 uLamp;
uniform vec3 uEmber;
varying vec2 vW;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main() {
  float lit = 0.0;
  float edge = 0.0;
  float eSum = 0.0;
  float fSum = 0.001;
  for (int i = 0; i < ${MAX_LIGHTS}; i++) {
    if (i >= uCount) break;
    vec4 l = uLights[i];
    float d = length(vW - l.xy) / l.z;
    // A long soft falloff with a slow flicker: light fading into the dark, no drawn ring.
    float f = 1.0 - smoothstep(0.45, 1.04 + 0.02 * sin(uTime * 7.0 + l.x * 3.0), d);
    float e = smoothstep(0.55, 0.85, d) * (1.0 - smoothstep(0.85, 1.05, d));
    // The brazier's red and a lantern's white blend where they meet, no seam.
    float g = f + e;
    eSum += g * step(1.5, l.w);
    fSum += g;
    lit = max(lit, f);
    edge = max(edge, e);
  }
  // Where pools overlap the edge between them is light, not a seam.
  edge *= 1.0 - smoothstep(0.6, 0.95, lit);
  float grain = hash(floor(vW * 6.0)) * 0.04;
  // Outside: the night sheet. Inside: the ground as it is, a breath of the light's colour where it thins out.
  vec3 tint = mix(uLamp, uEmber, eSum / fSum);
  // Two layers: the dark (gone where it's lit) over a faint breath of the light's colour where it thins out.
  float dark = (uDark + grain) * (1.0 - lit);
  float warm = edge * 0.14;
  float a = dark + warm * (1.0 - dark);
  vec3 c = (uNight * dark + tint * warm * (1.0 - dark)) / max(a, 0.001);
  gl_FragColor = vec4(c, a);
  #include <colorspace_fragment>
}`;

const LAMP = new THREE.Color('#ffe2c4');
const LAMP_HOT = new THREE.Color('#fff8ee');
const EMBER = new THREE.Color('#e0442c');
const EYE = new THREE.Color('#d8f0ff');
const EYE_HOT = new THREE.Color('#ff4f3a');
const MOTH = new THREE.Color('#fff4d8');

export const buildNight = (ctx: OmenCtx, radius: number): OmenScene => {
  const group = new THREE.Group();
  group.name = 'bet-night';
  const low = ctx.low;
  const sky = new SkyLook(ctx, {
    hemi: ['#3a4a6e', '#0a0c14', 0.3],
    key: ['#9fb4e6', 0.22],
    rim: ['#7f9cff', 1.1],
    background: '#06080e',
    fog: '#06080e',
    mist: { low: '#0b0f1a', mid: '#0a0d16', far: '#05070c', alpha: 0.9 },
  });
  // The sheet of darkness, laid on the ground (a grid that follows the terrain).
  const seg = low ? 36 : 64;
  const size = radius * 2.4;
  const geo = new THREE.PlaneGeometry(size, size, seg, seg);
  geo.rotateX(-Math.PI / 2);
  const p = geo.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) p.setY(i, ctx.ground(p.getX(i), p.getZ(i)) + 0.05);
  geo.computeBoundingSphere();
  const lights = Array.from({ length: MAX_LIGHTS }, () => new THREE.Vector4());
  const mat = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uLights: { value: lights },
      uCount: { value: 0 },
      uDark: { value: 0.8 },
      uTime: { value: 0 },
      uNight: { value: new THREE.Color('#03050b') },
      uLamp: { value: new THREE.Color('#f2d8bc') },
      uEmber: { value: new THREE.Color('#c0402c') },
    },
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  });
  const sheet = new THREE.Mesh(geo, mat);
  sheet.renderOrder = 4;
  sheet.frustumCulled = false;
  const glow = new GlowLayer(low ? 256 : 512);
  group.add(sheet, glow.mesh);

  let heroes: number[] = [];
  let hidden: { id: number; kind: string }[] = [];
  const node = ctx.at(0, 0);
  const rnd = looksRng(0x4e17);
  const mothSeed = Array.from({ length: 4 }, () => [rnd() * 6.28, 0.6 + rnd() * 0.8, 0.3 + rnd() * 0.5] as const);
  const right = new THREE.Vector3();
  const lamp = new THREE.Vector3();

  return {
    group,
    tick: (s: BattleState) => {
      heroes = s.heroes.filter((h) => h.downTicks === 0).map((h) => h.id);
      hidden = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall' && !isRevealed(s, e)).map((e) => ({ id: e.id, kind: e.kind }));
    },
    frame: (t, camera) => {
      (mat.uniforms.uTime as { value: number }).value = t;
      sky.pulse({});
      cameraRight(camera, right);
      glow.begin();
      // The node's brazier.
      let n = 0;
      const br = 0.94 + 0.06 * Math.sin(t * 5.3) + 0.03 * Math.sin(t * 13.1);
      // The brazier burns big and red; a lantern is small and white: told apart at a glance.
      (lights[n++] as THREE.Vector4).set(node.x, node.z, NODE_LIGHT, 2);
      glow.add(node.x, node.y + 0.9, node.z, EMBER, 2.4, 0.5 * br);
      glow.add(node.x, node.y + 0.85, node.z, EMBER, 0.9, 0.85 * br);
      glow.add(node.x, node.y + 0.1, node.z, EMBER, 4.5, 0.07 * br);
      for (const id of heroes) {
        const hp = ctx.pos(id);
        if (!hp) continue;
        const f = 0.9 + 0.07 * Math.sin(t * 7 + id) + 0.03 * Math.sin(t * 17.3 + id * 2);
        if (n < MAX_LIGHTS) (lights[n++] as THREE.Vector4).set(hp.x, hp.z, NIGHT_LANTERN, 1);
        // The lantern itself, held at the side (its pool on the ground is the sheet's warm hole).
        lamp.set(hp.x + right.x * 0.2, hp.y + 0.62, hp.z + right.z * 0.2);
        glow.add(lamp.x, lamp.y, lamp.z, LAMP_HOT, 0.3, 1.0 * f);
        glow.add(lamp.x, lamp.y, lamp.z, LAMP, 0.8, 0.45 * f);
        glow.add(hp.x, hp.y + 0.08, hp.z, LAMP, 3.2, 0.07 * f);
        // Moths round the flame.
        if (!low)
          for (let m = 0; m < mothSeed.length; m++) {
            const [ph, sp, rr] = mothSeed[m] as readonly [number, number, number];
            const a = t * sp * 3 + ph + id;
            glow.add(lamp.x + Math.cos(a) * rr, lamp.y + 0.15 + Math.sin(a * 1.7) * 0.18, lamp.z + Math.sin(a) * rr, MOTH, 0.07, 0.8);
          }
      }
      (mat.uniforms.uCount as { value: number }).value = n;
      // In the dark, only their eyes: two cold glints each, blinking now and then; a red one when close to a light.
      for (const e of hidden) {
        const ep = ctx.pos(e.id);
        if (!ep) continue;
        const y = ep.y + eyeHeight(e.kind);
        const blink = Math.sin(t * 0.8 + e.id * 2.3) > 0.95 ? 0.05 : 1;
        glow.add(ep.x - right.x * 0.05, y, ep.z - right.z * 0.05, EYE, 0.1, blink);
        glow.add(ep.x + right.x * 0.05, y, ep.z + right.z * 0.05, EYE, 0.1, blink);
        glow.add(ep.x, y, ep.z, EYE_HOT, 0.28, 0.35 * blink);
      }
      glow.end();
    },
    dispose: () => {
      sky.restore();
      geo.dispose();
      mat.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
